import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const STOP_MINUTES = 10;
const MOVING_SPEED_MS = 1.0;
const MOVED_METERS = 40;

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const ALERT_TEXT = {
  deviation: (n) => `${n} se ha desviado de la ruta`,
  speed: (n) => `${n} excede la velocidad permitida`,
  stopped: (n) => `${n} lleva más de ${STOP_MINUTES} min parado`,
  sos: (n) => `SOS: ${n} necesita ayuda`,
};

export function useRallyMasterAlerts(eventId, locations) {
  const [recent, setRecent] = useState([]);
  const seenAlertIds = useRef(new Set());
  const primed = useRef(false);
  const movement = useRef({});
  const stoppedNotified = useRef(new Set());

  const notify = useCallback((kind, name, key) => {
    setRecent(r => [{ kind, name, at: Date.now(), key }, ...r].slice(0, 8));
    toast.error(ALERT_TEXT[kind](name), { duration: 20000 });
  }, []);

  const dismiss = useCallback((key) => setRecent(r => r.filter(a => a.key !== key)), []);

  // Server alerts (deviation / speed): poll and announce only new ones
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/rally-master/events/${eventId}/alerts`, { headers: { Authorization: `Bearer ${token}` } });
        const alerts = (r.data?.alerts || r.data || []).filter(a => a.is_active && (a.alert_type === 'deviation' || a.alert_type === 'speed' || a.alert_type === 'sos'));
        if (!primed.current) {
          alerts.forEach(a => seenAlertIds.current.add(a.id));
          primed.current = true;
          return;
        }
        if (cancelled) return;
        alerts.forEach(a => {
          if (!seenAlertIds.current.has(a.id)) {
            seenAlertIds.current.add(a.id);
            notify(a.alert_type, a.pilot_name || a.user_name || 'Un piloto', a.id);
          }
        });
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [eventId, notify]);

  // Stopped detection from live positions
  useEffect(() => {
    const now = Date.now();
    locations.forEach(loc => {
      const m = movement.current[loc.user_id] || { lat: loc.latitude, lon: loc.longitude, lastMovingAt: now };
      const moved = haversine(m.lat, m.lon, loc.latitude, loc.longitude) > MOVED_METERS;
      const fast = (loc.speed || 0) > MOVING_SPEED_MS;
      if (moved || fast) {
        m.lat = loc.latitude; m.lon = loc.longitude; m.lastMovingAt = now;
        stoppedNotified.current.delete(loc.user_id);
      }
      m.stoppedFor = now - m.lastMovingAt;
      movement.current[loc.user_id] = m;
      const stale = now - new Date(loc.updated_at).getTime() > STOP_MINUTES * 60000;
      if (m.stoppedFor > STOP_MINUTES * 60000 && !stale && !stoppedNotified.current.has(loc.user_id)) {
        stoppedNotified.current.add(loc.user_id);
        notify('stopped', loc.user_name || 'Un piloto', `${loc.user_id}-stop`);
      }
    });
  }, [locations, notify]);

  const stoppedUserIds = new Set(Object.entries(movement.current).filter(([, m]) => m.stoppedFor > STOP_MINUTES * 60000).map(([id]) => id));

  return { recent, dismiss, stoppedUserIds };
}
